import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DupeOrderRow, OrderDetails } from '../../services/orders.service';
import ordersService from '../../services/orders.service';
import { useToast } from '../../contexts/ToastContext';
import { proDetailPath } from '../../utils/proSearch';
import PermissionsContainer from '../PermissionsContainer';
import WarningConfirmModal from '../ui/WarningConfirmModal';
import {
  ORDER_DUPE_COMPONENT_ID,
  useCanDupeAfterCreateDate,
} from '../../pages/pro/useCanDupeOrder';
import DupeOrderModal, { type DupeOrderFlags } from './DupeOrderModal';
import DupeOrderGrid from './DupeOrderGrid';

/* Dupe Order — the SaveBar action group that orchestrates the whole V1
 * dupepro → dupeprolist → dupeprosave flow (docs/plans/2026-08-13-dupe-order-
 * v1-rules.md). Structural twin of `OrderReuseActions`: eligibility computed
 * from `orderDetails`, the button wrapped in its own `PermissionsContainer`
 * (defaultHidden), `ensureSavedBeforeAction()` before anything runs, and every
 * follow-up surface (flags modal, grid, duplicate confirm) owned internally.
 *
 * A finished batch lands on Search Results filtered to the created pros — the
 * port of V1's customsearch redirect (v1-rules §4.6), reusing the Custom
 * Search deep link (`?searchBy=pronumber&q=<newline-joined pros>`, the same
 * URL GlobalSearch.handleCustomSearch builds). Navigating also closes the
 * drawer: on Search Results the drawer rides the `pro` URL param, and on any
 * other page the host page unmounts.
 */

interface OrderDupeActionsProps {
  orderDetails: OrderDetails;
  /** When true the button is disabled — used to defer the dupe while an
   * unrelated save is in flight. */
  busy?: boolean;
  /** True when the order form has pending unsaved edits. When set, Dupe runs
   * the order save first (via `onSaveBeforeAction`) and aborts if it's blocked
   * — see the save-first note on `onSaveBeforeAction`. */
  hasUnsavedChanges?: boolean;
  /** Runs the full order save flow and resolves `true` only when it's safe to
   * proceed (saved cleanly, or nothing to save). Dupe awaits this when there
   * are unsaved changes so the copy never runs against edits the user hasn't
   * committed. Mirrors V1, where "Dupe" was a submit button on the order edit
   * form: it set `returnto='dupepro'` and fired the standard save, and the save
   * response redirected to the dupe flow (v1-rules §1.5) — a failed save simply
   * never got there. Omit it (or `hasUnsavedChanges`) to fire immediately. */
  onSaveBeforeAction?: () => Promise<boolean>;
}

/** Which surface of the flow is showing. */
type DupeStep = 'idle' | 'flags' | 'grid';

/** The payload snapshot a duplicate-confirm resend replays verbatim. */
interface PendingSubmit {
  rows: DupeOrderRow[];
  nonce: string;
}

/**
 * Same idempotency convention as create (`useOrderEditing.ts:2644-2647`): one
 * nonce per ATTEMPT-SET, reused on the dupe-bypass resend so the backend's
 * (nonce, pronumber) key dedupes a double-submit.
 */
const makeSubmitNonce = (): string =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `dupe-${performance.now()}`;

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/**
 * V1's created-today gate (v1-rules §1.3), evaluated against the CLIENT
 * calendar. `createdDatetime` arrives pre-formatted "MM/DD/YYYY HH:MM" in
 * DB-server local time, so a user in another timezone can disagree with the
 * server about "today" for a few hours a day — harmless, because this is UX
 * only: the backend re-runs the gate on its own clock and 403s regardless of
 * what the button did. A blank createdatetime is NOT today, matching V1's
 * `substr($createdatetime,0,10) eq $todaydate` string compare.
 */
export const isCreatedToday = (createdDatetime?: string, now: Date = new Date()): boolean => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec((createdDatetime ?? '').trim());
  if (!m) return false;
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return m[1] === mm && m[2] === dd && m[3] === String(now.getFullYear());
};

/**
 * V1 button gate (`lib_editrecord.pl:816-827`, v1-rules §1) minus the two ACL
 * terms: `access_orders/dupe` is the `order-dupe` PermissionsContainer around
 * the button, and `dupeaftercreatedate` is `canDupeAfterCreateDate`.
 *
 * CLIENT ELIGIBILITY IS UX ONLY. `order_dupe.py` + `dupe_order_service` re-verify
 * every one of these server-side (403 on the ACLs / the created-today gate, 422
 * on a deleted or invoiced source), which is the authoritative check — this
 * function exists to keep an impossible button off the screen, not to enforce.
 */
export const computeDupeEligibility = (
  orderDetails: OrderDetails,
  canDupeAfterCreateDate: boolean,
  now: Date = new Date(),
): boolean => {
  // V1's `!$istemplate` (lib_editrecord.pl:820): templates ride the same drawer
  // (ProDetailContainer isTemplate) and the SaveBar renders its action groups for
  // them, so the gate must live here. The backend 404s a 'T…' pronumber anyway
  // (read_dupe_source reads orderscurrent) — this keeps the button off the screen.
  if (String(orderDetails.pronumber ?? '').trim().toUpperCase().startsWith('T')) {
    return false;
  }
  // `addlInfoDeleted` IS ordersall.deleted (orders.py:3771 emits `deleted or ''`).
  // Blank means the column was never set, which V1 stores as 'N' — treat only an
  // explicit non-'N' value as deleted so a sparse record doesn't lose the button.
  const deleted = (orderDetails.addlInfoDeleted ?? '').trim().toUpperCase();
  if (deleted !== '' && deleted !== 'N') return false;
  // V1 reads ordersall.invoicedate; 360 surfaces the main invoice's date on the
  // invoiceMain section (null when the order has no non-deleted main invoice).
  if ((orderDetails.invoiceMain?.invoiceDate ?? '').trim() !== '') return false;
  return isCreatedToday(orderDetails.createdDatetime, now) || canDupeAfterCreateDate;
};

const OrderDupeActions: React.FC<OrderDupeActionsProps> = ({
  orderDetails,
  busy = false,
  hasUnsavedChanges = false,
  onSaveBeforeAction,
}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const canDupeAfterCreateDate = useCanDupeAfterCreateDate();

  const [step, setStep] = useState<DupeStep>('idle');
  const [flags, setFlags] = useState<DupeOrderFlags | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState<PendingSubmit | null>(null);
  const [dupeConfirm, setDupeConfirm] = useState<Array<{ row: number; pros: Array<string | number> }> | null>(null);

  const eligible = computeDupeEligibility(orderDetails, canDupeAfterCreateDate);

  /** Everything back to closed — used by Cancel, by a nonce replay, and after
   * a finished batch hands off to Search Results. */
  const reset = useCallback(() => {
    setStep('idle');
    setFlags(null);
    setSubmitting(false);
    setPending(null);
    setDupeConfirm(null);
  }, []);

  // Save-before-dupe gate, verbatim from OrderReuseActions:83-86. Resolves
  // `true` when the order saved cleanly or had nothing to save; `false` when the
  // save was blocked (validation, stale conflict, deadline/override cancel,
  // network error), in which case the flow never opens.
  const ensureSavedBeforeAction = useCallback(async (): Promise<boolean> => {
    if (!hasUnsavedChanges || !onSaveBeforeAction) return true;
    return onSaveBeforeAction();
  }, [hasUnsavedChanges, onSaveBeforeAction]);

  const handleDupeClick = useCallback(async () => {
    const saved = await ensureSavedBeforeAction();
    if (!saved) return;
    setStep('flags');
  }, [ensureSavedBeforeAction]);

  /** The source order's per-column display values — V1's `dupevalue_<field>`
   * header row (v1-rules §3.2). The two appointment numbers come off the legs:
   * V1 takes the LAST matching leg (its loop overwrites as it scans), so a
   * multi-pickup / multi-delivery order uses the final one. */
  const sourceValues: DupeOrderRow = useMemo(() => {
    const legs = orderDetails.legs ?? [];
    let origapptnumber = '';
    let destapptnumber = '';
    for (const leg of legs) {
      if ((leg.legtype ?? '').trim().toUpperCase() === 'PICKUP') {
        origapptnumber = str(leg.origApptNumber);
      }
      if ((leg.legAction2 ?? '').trim().toUpperCase() === 'DELIVERY') {
        destapptnumber = str(leg.destApptNumber);
      }
    }
    return {
      container: str(orderDetails.container),
      // ordersall.bol surfaces as `bookingNumber` on OrderDetails (orders.py:3658).
      bol: str(orderDetails.bookingNumber),
      sealnumber: str(orderDetails.sealNumber),
      chassisid: str(orderDetails.chassisId),
      commodity: str(orderDetails.commodity),
      piececount: str(orderDetails.pieceCount),
      lbs: str(orderDetails.lbs),
      kgs: str(orderDetails.kgs),
      metrictons: str(orderDetails.metricTons),
      po: str(orderDetails.purchaseOrder),
      // billtorefno lives on the main invoice, not the order.
      billtorefno: str(orderDetails.invoiceMain?.billToRefNo),
      etadate: str(orderDetails.etaDate),
      targetonorby: str(orderDetails.targetOnOrBy),
      targetdeliverydate: str(orderDetails.targetDeliveryDate),
      lastfreedaydate: str(orderDetails.lastFreeDay),
      cutoffdatetime: str(orderDetails.cutoffDatetime),
      origapptnumber,
      destapptnumber,
    };
  }, [orderDetails]);

  // V1 posts the literal "NEW MASTER" sentinel when samemaster='N' (v1-rules
  // §3.2); the grid only shows it, the server resolves it.
  const masterProLabel = flags?.samemaster === 'N'
    ? 'NEW MASTER'
    : str(orderDetails.masterPro);

  const submitBatch = useCallback(async (
    rows: DupeOrderRow[],
    nonce: string,
    bypassDupeCheck: boolean,
    opts: DupeOrderFlags,
  ) => {
    setSubmitting(true);
    setDupeConfirm(null);
    try {
      const res = await ordersService.dupeOrder(orderDetails.pronumber, {
        samemaster: opts.samemaster,
        copymrbl: opts.copymrbl,
        copynotes: opts.copynotes,
        copyaccessorials: opts.copyaccessorials,
        copydocuments: opts.copydocuments,
        // The grid owns the final row count (rows can be added/removed after the
        // flags modal), and the server rejects dupecount != len(rows).
        dupecount: rows.length,
        rows,
        submitNonce: nonce,
        bypassDupeCheck,
        // The grid pre-fills every cell with the value that will copy, so what
        // rides in `rows` is literal: blank means blank, never "inherit".
        explicitValues: true,
      });

      if (res?.requiresConfirmation) {
        // Nothing was written. Keep the grid up (busy off) behind the confirm so
        // Cancel drops the user back on their typing. The resend reuses THIS
        // nonce: the server claims the nonce only after the pre-check passes
        // (dupe_order_service.py:1393-1455), so it is still unclaimed here.
        setPending({ rows, nonce });
        setDupeConfirm(res.dupes ?? []);
        return;
      }

      if (res?.duplicate) {
        // Nonce replay — the batch already landed on an earlier submit.
        showToast({
          type: 'warning',
          message: res.message || 'Duplicate submission — this duplicate was already created.',
          duration: 6000,
        });
        reset();
        return;
      }

      // Batch result (possibly partial — `failed` is per row). Failures and
      // saverule messages surface as toasts, then the created pros hand off to
      // Search Results — the port of V1's customsearch redirect (v1-rules
      // §4.6; V1 also listed the SOURCE pro, this lists the created ones).
      const created = res?.created ?? [];
      const failed = res?.failed ?? [];
      for (const f of failed) {
        // Rows come back 0-based (dupe_order_service.py:1553).
        showToast({
          type: 'error',
          message: `Row ${f.row + 1} was not created: ${f.error}`,
          duration: 8000,
        });
      }
      for (const m of res?.userMessages ?? []) {
        showToast({
          type: m.level === 'error' ? 'error' : 'warning',
          message: m.text,
          duration: 6000,
        });
      }
      if (created.length === 0) {
        // Nothing landed — keep the grid open so the rows survive a retry.
        return;
      }
      showToast({
        type: 'success',
        message: `Created ${created.length} pro${created.length === 1 ? '' : 's'}`,
        duration: 4000,
      });
      // The same Custom Search deep link GlobalSearch builds: newline-joined
      // `q` + searchBy (SearchResults splits `q` on newlines in that mode).
      // Navigating closes the drawer — `pro` rides the URL on Search Results,
      // and any other host page unmounts.
      const params = new URLSearchParams({
        q: created.join('\n'),
        searchBy: 'pronumber',
      });
      reset();
      navigate(`/searchresults?${params.toString()}`);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' && detail
        ? detail
        : e?.message || 'Duplicating the order failed.';
      // The grid stays open so the entered rows survive a real 4xx/5xx.
      showToast({ type: 'error', message: `Dupe failed: ${msg}`, duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  }, [orderDetails.pronumber, showToast, reset, navigate]);

  const handleGridSubmit = useCallback((rows: DupeOrderRow[]) => {
    if (!flags) return;
    void submitBatch(rows, makeSubmitNonce(), false, flags);
  }, [flags, submitBatch]);

  const handleConfirmDupes = useCallback(() => {
    if (!pending || !flags) { setDupeConfirm(null); return; }
    void submitBatch(pending.rows, pending.nonce, true, flags);
  }, [pending, flags, submitBatch]);

  return (
    <>
      {eligible && (
        <PermissionsContainer
          componentId={ORDER_DUPE_COMPONENT_ID}
          componentName="Duplicate Order"
          defaultHidden
        >
          <button
            type="button"
            onClick={() => void handleDupeClick()}
            disabled={busy || submitting}
            title="Create copies of this order"
            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded-md transition-colors"
          >
            Dupe
          </button>
        </PermissionsContainer>
      )}

      <DupeOrderModal
        open={step === 'flags'}
        sourcePro={orderDetails.pronumber}
        onClose={reset}
        onContinue={(opts) => { setFlags(opts); setStep('grid'); }}
      />

      {flags && (
        <DupeOrderGrid
          open={step === 'grid'}
          busy={submitting}
          sourceValues={sourceValues}
          masterProLabel={masterProLabel}
          movetype={orderDetails.moveType ?? ''}
          copyFlags={{
            copymrbl: flags.copymrbl,
            copynotes: flags.copynotes,
            copyaccessorials: flags.copyaccessorials,
            copydocuments: flags.copydocuments,
          }}
          dupecount={flags.dupecount}
          onSubmit={handleGridSubmit}
          onBack={() => setStep('flags')}
          onClose={reset}
        />
      )}

      {dupeConfirm && (
        <WarningConfirmModal
          open
          title="Possible Duplicate Order"
          message={
            <>
              <p className="mb-2">
                {dupeConfirm.length === 1 ? 'A row' : 'Some rows'} may already exist as
                {dupeConfirm.length === 1 ? ' an order' : ' orders'}:
              </p>
              <ul className="mb-2 list-disc pl-5">
                {dupeConfirm.map((d) => (
                  <li key={d.row}>
                    {/* Rows come back 0-based (dupe_order_service.py:1418). */}
                    Row {d.row + 1}: possible duplicate of pro(s){' '}
                    {d.pros.map((p, i) => (
                      <React.Fragment key={String(p)}>
                        {i > 0 && ', '}
                        <a
                          href={proDetailPath(p)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {p}
                        </a>
                      </React.Fragment>
                    ))}
                  </li>
                ))}
              </ul>
              Open them in a new tab to compare, then create these orders anyway?
            </>
          }
          confirmLabel="Create Anyway"
          // NOT "Back" — the grid is still mounted underneath with its own Back
          // button, and two identically-labelled buttons on screen is a trap.
          cancelLabel="Cancel"
          onConfirm={handleConfirmDupes}
          onCancel={() => setDupeConfirm(null)}
        />
      )}
    </>
  );
};


export default OrderDupeActions;
